import { Tabs, styled, Tab, Box, Typography } from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { REG_CHANGE_STATUS } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { RootState } from 'common/store/AppStore';
import { setRegChangeSelectedTabIndex } from 'common/store/reducer/commonReducer';
import { formatNumber } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { handleSpecialCharacters } from 'features/RiskProfile/Taxonomy/TaxonomyUtils';

import { getRegChangeTableData } from '../../RegulationChange/api';
import {
	IRegulationIncomingData,
	IRegulationListTable,
} from '../../RegulationChange/api/type-def';

import IncomingTable from './IncomingTable/IncomingTable';
import ListTable from './ListTable';

import styles from './RegulationChangeListTable.module.scss';

const {
	tabsBackground,
	tabTextColor,
	selectedTabTextColor,
	selectedTabBackground,
} = styles;

const { OK } = HTTP_STATUS_CODES;

interface StyledTabsProps {
	children?: React.ReactNode;
	value: number;
	onChange: (event: React.SyntheticEvent, newValue: number) => void;
}
const StyledTabs = styled((props: StyledTabsProps) => (
	<Tabs
		{...props}
		TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
	/>
))({
	padding: '4px',
	backgroundColor: tabsBackground,
	minHeight: '43px',
	width: 'max-content',
	borderRadius: '4px',
	marginTop: '19px',
	'& .MuiTabs-indicator': {
		display: 'flex',
		justifyContent: 'center',
		backgroundColor: 'transparent',
	},
});
interface StyledTabProps {
	label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
	<Tab disableRipple {...props} />
))(({ theme }) => ({
	textTransform: 'none',
	fontWeight: theme.typography.fontWeightRegular,
	minHeight: '1.5rem',
	fontSize: '16px',
	color: tabTextColor,
	padding: '0px 10px',
	margin: '2px 2px 1.5px 2px',
	'&.Mui-selected': {
		color: selectedTabTextColor,
		height: '32px',
		alignItems: 'center',
		gap: '8px',
		backgroundColor: selectedTabBackground,
		borderRadius: '2px',
		minHeight: '1.5rem',
	},
	'&.Mui-focusVisible': {
		backgroundColor: selectedTabBackground,
	},
}));
interface TabPanelProps {
	children: React.ReactNode;
	index: number;
	value: number;
}

const TabPanel = (props: TabPanelProps) => {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`full-width-tabpanel-${index}`}
			aria-labelledby={`full-width-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box>
					<Typography>{children}</Typography>
				</Box>
			)}
		</div>
	);
};
interface IRegulationChangeData {
	inprogress: IRegulationListTable[];
	incoming: IRegulationListTable[];
	pastdue: IRegulationListTable[];
	completed: IRegulationListTable[];
}
interface IRegulationChangeCountData {
	inprogress: number;
	incoming: number;
	pastdue: number;
	completed: number;
}

const RegulationChangeListTable = () => {
	const dispatch = useDispatch();
	const [value, setValue] = useState<number>(0);
	const [incomingTableData, setIncomingTableData] = useState<
		IRegulationIncomingData[]
	>([]);
	const [totalIncomingRecordsCount, setTotalIncomingRecordCount] =
		useState<number>(0);
	const [citationData, setCitationData] = useState<IRegulationChangeData>(
		{} as IRegulationChangeData,
	);

	const [isDataLoading, setDataLoading] = useState<any>({
		inprogress: true,
		pastdue: true,
		completed: true,
		incoming: true,
	});

	const [citationCountData, setCitationCountData] =
		useState<IRegulationChangeCountData>({
			inprogress: 0,
			incoming: 0,
			pastdue: 0,
			completed: 0,
		} as IRegulationChangeCountData);

	const {
		tableQueryObj: inprogressTableQuery,
		updateTableQuery: updateInprogressTableQuery,
		queryFlag,
		updateQueryFlag,
	} = useTableQuery({ id: 'RegChangeInprogressTable' });
	const {
		tableQueryObj: incomingTableQuery,
		updateTableQuery: updateIncomingTableQuery,
	} = useTableQuery({ id: 'RegChangeIncomingTable' });
	const {
		tableQueryObj: pastDueTableQuery,
		updateTableQuery: updatePastDueTableQuery,
	} = useTableQuery({ id: 'RegChangePastDueTable' });
	const {
		tableQueryObj: completeTableQuery,
		updateTableQuery: updateCompleteTableQuery,
	} = useTableQuery({ id: 'RegChangeCompleteTable' });

	const searchText = useSelector(
		(state: RootState) => state.common.regChangeSearchText,
	);
	const tabIndex = useSelector(
		(state: RootState) => state.common.regChangeSelectedTabIndex,
	);

	const handleChange = useCallback(
		(event: React.SyntheticEvent, newValue: number) => {
			setValue(newValue);
			dispatch(setRegChangeSelectedTabIndex(newValue));
		},
		[setValue],
	);

	const handlePageChange = (data: any, type: string, flag?: boolean) => {
		if (flag) {
			const { updateStoreQueryFn } = getTableHelper(type);
			updateStoreQueryFn?.(data);
			fetchRegChangeSearchTableData(data, type);
		}
	};

	const handleSortChange = (data: any, type: string) => {
		fetchRegChangeSearchTableData(data, type);
	};

	const fetchRegChangeSearchTableData = async (data: any, type: string) => {
		setDataLoading((previous: any) => ({ ...previous, [type]: true }));
		const apiData = {
			...data,
		};
		let paramsObj = {};
		const updatedSearchString = handleSpecialCharacters(searchText);
		if (type === 'incoming') {
			paramsObj = {
				searchText: updatedSearchString,
				index: `reg-change-incoming-${process.env.REACT_APP_ENVIRONMENT}`,
			};
		} else {
			paramsObj = { searchText };
		}
		const response = await getRegChangeTableData(paramsObj, apiData, type);
		if (response?.statusCode === OK) {
			const responseData = response?.responseBody;
			const tempTableData =
				response?.responseBody?.content === null
					? []
					: response?.responseBody?.content;
			if (tempTableData && type !== 'incoming') {
				setCitationData((previous) => ({ ...previous, [type]: tempTableData }));
				setCitationCountData((previous) => ({
					...previous,
					[type]: responseData?.totalCount,
				}));
				setDataLoading((previous: any) => ({ ...previous, [type]: false }));
			} else if (tempTableData && type === 'incoming') {
				setIncomingTableData(tempTableData);
				setTotalIncomingRecordCount(responseData?.totalCount);
				setDataLoading((previous: any) => ({ ...previous, [type]: false }));
			}
		} else {
			setDataLoading((previous: any) => ({ ...previous, [type]: false }));
		}
	};

	const getTableHelper = (status: string) => {
		switch (status) {
			case REG_CHANGE_STATUS.IN_PROGRESS:
				return {
					storeTableQuery: inprogressTableQuery,
					updateStoreQueryFn: updateInprogressTableQuery,
				};
			case REG_CHANGE_STATUS.IN_COMING:
				return {
					storeTableQuery: incomingTableQuery,
					updateStoreQueryFn: updateIncomingTableQuery,
				};
			case REG_CHANGE_STATUS.PAST_DUE:
				return {
					storeTableQuery: pastDueTableQuery,
					updateStoreQueryFn: updatePastDueTableQuery,
				};
			case REG_CHANGE_STATUS.COMPLETED:
				return {
					storeTableQuery: completeTableQuery,
					updateStoreQueryFn: updateCompleteTableQuery,
				};
			default:
				return {};
		}
	};

	useEffect(() => {
		const defaultQuery = {
			pageNumber: 0,
			pageSize: 10,
		};
		const statuses = ['inprogress', 'incoming', 'pastdue', 'completed'];
		statuses.forEach((status) => {
			const { storeTableQuery, updateStoreQueryFn } = getTableHelper(status);
			if (queryFlag) {
				fetchRegChangeSearchTableData(storeTableQuery, status);
				updateQueryFlag(false);
			} else if (searchText) {
				fetchRegChangeSearchTableData(defaultQuery, status);
				updateStoreQueryFn?.(defaultQuery);
			} else {
				fetchRegChangeSearchTableData(storeTableQuery, status);
			}
		});
		setValue(tabIndex);
	}, [searchText]);

	return (
		<div className={styles['changes-for-your-attention-block']}>
			<StyledTabs
				value={value}
				onChange={handleChange}
				aria-label="scrollable prevent tabs example"
			>
				<StyledTab
					key="regulation-change-in-progress"
					label={`In progress (${formatNumber(citationCountData.inprogress)})`}
					data-testid="in-progress"
				/>
				<StyledTab
					key="regulation-change-incoming"
					label={`Incoming (${formatNumber(totalIncomingRecordsCount)})`}
					data-testid="incoming"
				/>
				<StyledTab
					key="regulation-change-pastdue"
					label={`Past due (${formatNumber(citationCountData.pastdue)})`}
					data-testid="past-due"
				/>
				<StyledTab
					key="regulation-change-completed"
					label={`Completed (${formatNumber(citationCountData.completed)})`}
					data-testid="completed"
				/>
			</StyledTabs>
			<TabPanel value={value} index={0}>
				<ListTable
					defaultQuery={inprogressTableQuery}
					data={citationData.inprogress || []}
					isLoading={isDataLoading.inprogress}
					key="inprogress"
					totalRecordsCount={citationCountData.inprogress}
					handlePageChange={(e, f) => handlePageChange(e, 'inprogress', !!f)}
					handleSortChange={(e) => handleSortChange(e, 'inprogress')}
				/>
			</TabPanel>
			<TabPanel value={value} index={1}>
				<IncomingTable
					defaultQuery={incomingTableQuery}
					data={incomingTableData || []}
					isLoading={isDataLoading.incoming}
					totalRecordsCount={totalIncomingRecordsCount}
					key="incoming"
					handlePageChange={(e, f) => {
						handlePageChange(e, 'incoming', !!f);
					}}
					handleSortChange={(e) => {
						handleSortChange(e, 'incoming');
					}}
				/>
			</TabPanel>
			<TabPanel value={value} index={2}>
				<ListTable
					defaultQuery={pastDueTableQuery}
					data={citationData.pastdue || []}
					isLoading={isDataLoading.pastdue}
					totalRecordsCount={citationCountData.pastdue}
					key="pastdue"
					handlePageChange={(e, f) => {
						handlePageChange(e, 'pastdue', !!f);
					}}
					handleSortChange={(e) => {
						handleSortChange(e, 'pastdue');
					}}
				/>
			</TabPanel>
			<TabPanel value={value} index={3}>
				<ListTable
					defaultQuery={completeTableQuery}
					data={citationData.completed || []}
					isLoading={isDataLoading.completed}
					totalRecordsCount={citationCountData.completed}
					key="completed"
					handlePageChange={(e, f) => {
						handlePageChange(e, 'completed', !!f);
					}}
					handleSortChange={(e) => {
						handleSortChange(e, 'completed');
					}}
				/>
			</TabPanel>
		</div>
	);
};

export default RegulationChangeListTable;
